<template>
  <ValidationObserver
    v-slot="{handleSubmit, invalid}"
    ref="form"
  >
    <b-modal
      id="folderModal"
      v-model="modal.show"
      :title="getCurrentTitle"
      @hidden="onHidden"
      @hide="beforeHide"
    >
      <b-alert
        :show="!!errorMessage"
        variant="danger"
        dismissible
      >
        {{ errorMessage }}
      </b-alert>
      <content-spinner
        :show="loading"
        text="Please wait..."
      />
      <b-form
        :class="{ hide: loading }"
        @submit.prevent="handleSubmit(onSubmit)"
        @reset="handleSubmit(onReset)"
      >
        <input-widget
          id="createFolderName"
          :model="form"
          :field="fields.folderName"
          :autofocus="true"
        />
        <input-widget
          :model="form"
          :field="fields.description"
        />
        <vfa-picker
          v-model="form.icon"
          :only="onlyIcons"
          class="icon-select-input"
        >
          <template #activator="{ on }">
            <b-button
              id="selectFolderIconBtn"
              variant="warning"
              @click="on"
            >
              Choose Icon
            </b-button>
            <font-awesome-icon
              v-if="form.icon"
              id="selectFolderIcon"
              class="selected-icon"
              :icon="['fas', form.icon]"
            />
          </template>
        </vfa-picker>
      </b-form>
      <div
        slot="modal-footer"
        class="d-flex"
      >
        <b-button
          id="folderModalCancel"
          class="mt-3 mr-3"
          block
          @click="$bvModal.hide('folderModal')"
        >
          Cancel
        </b-button>
        <b-button
          id="folderModalOk"
          class="mt-3"
          variant="primary"
          :disabled="invalid"
          block
          @click.prevent="handleSubmit(onSubmit);"
        >
          OK
        </b-button>
      </div>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../../../../core/components/ContentSpinner';
import InputWidget from '../../../../../core/components/complex-table/components/InputWidget';
import {v4 as uuidv4} from 'uuid';

const {mapState, mapActions} = createNamespacedHelpers('dataManagement');
const {mapActions: sidebarMapActions} = createNamespacedHelpers('sidebar');
export default {
  name: 'FolderModal',
  components: {InputWidget, ContentSpinner},
  data() {
    return {
      errorMessage: null,
      form: {
        label: '',
        description: '',
        moduleUuid: '',
        icon: '',
      },
      fields: {
        folderName: {
          type: 'text',
          required: true,
          label: 'Name',
          name: 'label',
        },
        description: {
          type: 'textarea',
          required: false,
          label: 'Description',
          name: 'description',
        },
      },
    };
  },
  watch: {
    folderData: {
      handler(val) {
        if (val instanceof Event) return;
        this.form = val;
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    ...mapState(['onlyIcons']),
    ...mapState({
      modal: state => state.folderModal,
      folderData: state => state.folderModal.data,
      loading: state => state.folderModal.loading,
      permissionTypes: state => state.permissionTypes,
    }),
    getCurrentTitle() {
      return this.folderData.moduleGuid
        ? `Update folder "${this.folderData.label}"`
        : 'Create Folder';
    },
  },
  methods: {
    ...mapActions([
      'hideFolderModal',
      'createFolder',
      'updateFolder',
      'restoreFolder',
      'getFolders',
    ]),
    ...sidebarMapActions(['getSidebarStructure']),
    async onSubmit() {
      let action = 'update';
      this.form.moduleGuid = this.$route.params.moduleUuid;
      this.form.module = this.$route.params.moduleUuid;

      const params = {
        folderGuid: this.form.folderGuid,
        folderName: this.form.label,
        description: this.form.description,
        isActive: true,
        parentFolderGuid: null,
        moduleGuid: this.form.moduleGuid,
        folderIcon: this.form.icon,
      };

      if (!this.form.folderGuid) {
        params.currentUserPermissionLevel = this.permissionTypes.admin;
        params.folderGuid = uuidv4();
        action = 'create';
      }

      let response;
      if (action === 'create') {
        response = await this.createFolder(params);
      } else {
        response = await this.updateFolder(params);
      }
      this.errorMessage = response.body.ErrorMessage;
      if (response.success) {
        this.$toasted.success(`Folder "${this.form.label}" was ${action}d`, {
          duration: TOASTER_DURATION,
        });

        this.hideFolderModal();
        this.$emit('on-folder-save');
        await this.getSidebarStructure();
        this.$forceUpdate();
      } else if (response.body.isDeleted) {
        await this.showRestoreFolderModal(this.form.label, response.body.folderGuid);
        await this.getSidebarStructure();
      } else {
        this.$toasted.error(response.body, {
          duration: TOASTER_DURATION,
        });
      }
    },
    async showRestoreFolderModal(folderName, folderUuid) {
      const success = await this.$confirm(
        'Restore Folder...',
        `Folder with the name '${folderName}' already exists in the 'Recycle Bin' would you like to restore?`,
        null,
        null,
        null,
        [{
          title: 'Recycle Bin',
          variant: 'success',
          callback: (hideModal) => {
            if (hideModal) {
              hideModal();
            }
            this.hideFolderModal();
            this.$router.push({name: 'recycleBin', query: {search: folderName}});
          },
        }]);

      if (success) {
        const restoreFolder = await this.restoreFolder({id: folderUuid, restrictFetch: true});
        if (restoreFolder.success) {
          await this.getFolders(this.form.moduleUuid);
          this.hideFolderModal();
        }
      }
    },
    beforeHide(e) {
      this.errorMessage = '';
      if (this.loading) {
        e.preventDefault();
        return false;
      }
    },
    onHidden() {
      this.form = {};
      this.hideFolderModal();
    },
  },
};
</script>

<style scoped lang="scss">
.icon-select-input {
  display: flex;
  align-items: center;
  gap: 10px;


  & .selected-icon {
    width: 20px;
    height: 20px;
    padding: 5px;
    border: 1px solid #ced4da;
  }
}

.hide {
  display: none;
}
</style>